// @shared

interface TelegramThemeParams {
  bg_color?: string
  secondary_bg_color?: string
  text_color?: string
  hint_color?: string
  button_color?: string
  button_text_color?: string
}

interface TelegramBackButton {
  show(): void
  hide(): void
  onClick(callback: () => void): void
  offClick?(callback: () => void): void
}

interface TelegramHapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void
  notificationOccurred(type: 'error' | 'success' | 'warning'): void
  selectionChanged(): void
}

interface TelegramWebApp {
  initData: string
  initDataUnsafe?: { start_param?: string }
  platform?: string
  version?: string
  themeParams: TelegramThemeParams
  colorScheme: 'light' | 'dark'
  BackButton: TelegramBackButton
  HapticFeedback?: TelegramHapticFeedback
  ready(): void
  expand(): void
  requestFullscreen?(): void
  close(): void
  showConfirm?(message: string, callback: (confirmed: boolean) => void): void
  showAlert?(message: string, callback?: () => void): void
  openTelegramLink?(url: string): void
  openLink?(url: string, options?: { try_instant_view?: boolean }): void
  shareToStory?(mediaUrl: string, params?: {
    text?: string
    widget_link?: { url: string; name?: string }
  }): void
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

/** Параметр startapp из ссылки бота (кнопка «Открыть» под сообщением). */
export function getTelegramStartParam(): string {
  if (typeof window === 'undefined') return ''
  return window.Telegram?.WebApp?.initDataUnsafe?.start_param ?? ''
}

export function getTelegramInitData(): string {
  if (typeof window === 'undefined') return ''
  return window.Telegram?.WebApp?.initData ?? ''
}

export function initTelegramRootPage(): void {
  if (typeof window === 'undefined') return
  const webApp = window.Telegram?.WebApp
  if (!webApp) return

  webApp.BackButton.hide()
  webApp.ready()
  const platform = (webApp.platform || '').toLowerCase()
  if (platform === 'android' || platform === 'ios') {
    webApp.expand()
    try {
      if (typeof webApp.requestFullscreen === 'function') webApp.requestFullscreen()
    } catch (_) {}
  }
}

// Стек обработчиков: снизу — навигация по экранам (setTelegramBackHandler),
// выше — уровни внутри экрана (список → результат, дерево вопросов, форма → проверка),
// на самом верху — модалки вроде полноэкранной картинки. Пока сверху что-то лежит,
// телеграмовская кнопка возвращает на уровень ниже, а не закрывает приложение.
const backHandlerStack: Array<(() => void) | null> = []
// Подписываемся на кнопку ровно один раз: offClick в Bot API опционален, и на
// клиентах без него перепривязка копила бы обработчики — один клик срабатывал бы
// за несколько уровней сразу.
let backDispatcherBound = false

function currentBackHandler(): (() => void) | null {
  for (let index = backHandlerStack.length - 1; index >= 0; index -= 1) {
    const handler = backHandlerStack[index]
    if (handler) return handler
  }
  return null
}

function dispatchBack(): void {
  currentBackHandler()?.()
}

function syncBackButton(): void {
  if (typeof window === 'undefined') return
  const backButton = window.Telegram?.WebApp?.BackButton
  if (!backButton) return

  if (!backDispatcherBound) {
    backButton.onClick(dispatchBack)
    backDispatcherBound = true
  }
  if (currentBackHandler()) backButton.show()
  else backButton.hide()
}

export function setTelegramBackHandler(handler: (() => void) | null): void {
  backHandlerStack[0] = handler
  syncBackButton()
}

/** Временно забрать кнопку «назад» себе. Возвращает функцию, которая её отдаёт обратно. */
export function pushTelegramBackHandler(handler: () => void): () => void {
  if (backHandlerStack.length === 0) backHandlerStack.push(null)
  backHandlerStack.push(handler)
  syncBackButton()

  let released = false
  return () => {
    if (released) return
    released = true
    const index = backHandlerStack.lastIndexOf(handler)
    if (index > 0) backHandlerStack.splice(index, 1)
    syncBackButton()
  }
}

export function telegramImpact(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  if (typeof window === 'undefined') return
  try {
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style)
  } catch (_) {}
}

export function telegramNotify(type: 'error' | 'success' | 'warning'): void {
  if (typeof window === 'undefined') return
  try {
    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type)
  } catch (_) {}
}

/** Подтверждение действия: нативный диалог Telegram, вне мини-аппа — window.confirm. */
export function telegramConfirm(message: string): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false)
  return new Promise(resolve => {
    const webApp = window.Telegram?.WebApp
    try {
      if (webApp?.showConfirm) {
        webApp.showConfirm(message, confirmed => resolve(Boolean(confirmed)))
        return
      }
    } catch (_) {}
    resolve(window.confirm(message))
  })
}

export function openGetKarmaBot(): void {
  if (typeof window === 'undefined') return
  const url = 'https://t.me/getkarma_bot'
  const webApp = window.Telegram?.WebApp
  if (webApp?.openTelegramLink) webApp.openTelegramLink(url)
  else window.location.href = url
}

export function openTelegramUrl(url: string): void {
  if (typeof window === 'undefined' || !/^https:\/\/t\.me\/[A-Za-z0-9_/%?=&.-]+$/.test(url)) return
  const webApp = window.Telegram?.WebApp
  if (webApp?.openTelegramLink) webApp.openTelegramLink(url)
  else window.location.href = url
}

export function openExternalUrl(url: string): void {
  if (typeof window === 'undefined' || !/^https:\/\//.test(url)) return
  const webApp = window.Telegram?.WebApp
  if (webApp?.openLink) {
    webApp.openLink(url, { try_instant_view: false })
    return
  }
  const opened = window.open(url, '_blank', 'noopener,noreferrer')
  if (!opened) window.location.assign(url)
}

export function canShareTelegramStory(): boolean {
  if (typeof window === 'undefined') return false
  return typeof window.Telegram?.WebApp?.shareToStory === 'function'
}

export function shareTelegramStory(
  mediaUrl: string,
  params: { text: string; widgetUrl: string },
): boolean {
  if (typeof window === 'undefined') return false
  if (!/^https:\/\//.test(mediaUrl) || !/^https:\/\/t\.me\//.test(params.widgetUrl)) return false
  const webApp = window.Telegram?.WebApp
  if (typeof webApp?.shareToStory !== 'function') return false
  try {
    webApp.shareToStory(mediaUrl, {
      text: params.text,
      widget_link: {
        url: params.widgetUrl,
        name: 'Открыть GetKarma',
      },
    })
    return true
  } catch (_) {
    return false
  }
}
